import React, { useState, useRef, useEffect } from 'react';
import { ShortCutKey, TooltipContainer, TooltipContent } from './styles';
import { useMobileDevice } from '../../global/useMobile';
import { createPortal } from 'react-dom';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

interface TooltipProps {
  title: React.ReactNode;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
  maxWidth: string | number;
  isInModal?: boolean;
  isVisibleContent?: boolean;
  className?: string;
  shortCutKey?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  placement = 'bottom',
  maxWidth,
  isInModal = false,
  className,
  isVisibleContent,
  shortCutKey
}) => {
  //  state variables
  const [isVisible, setIsVisible] = useState(false);
  const [arrowPosition, setArrowPosition] = useState({ left: '50%', top: '0' });
  const [actualPlacement, setActualPlacement] = useState(placement);
  // ref variables
  const tooltipRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  // mobx store variable
  const { appStore } = rootStore;
  const { themeMode } = appStore || {};
  // other Variables
  const isMobile = useMobileDevice();

  // mouse Enter and Leave will only work for desktop
  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };
  // this will only work for responsive devices
  const handleClick = () => {
    if (isMobile) {
      setIsVisible(!isVisible);
    }
  };

  // Close tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isVisible) {
        setIsVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible]);

  useEffect(() => {
    const calculatePosition = () => {
      if (!tooltipRef.current || !targetRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const spacing = 12;

      // Check available space for flipping
      let newPlacement = placement;
      if (placement === 'bottom') {
        const spaceBelow = window.innerHeight - targetRect.bottom;
        if (spaceBelow < tooltipRect.height + spacing) {
          newPlacement = 'top';
        }
      } else if (placement === 'top') {
        const spaceAbove = targetRect.top;
        if (spaceAbove < tooltipRect.height + spacing) {
          newPlacement = 'bottom';
        }
      } else if (placement === 'left') {
        const spaceLeft = targetRect.left;
        if (spaceLeft < tooltipRect.width + spacing) {
          newPlacement = 'right';
        }
      } else if (placement === 'right') {
        const spaceRight = window.innerWidth - targetRect.right;
        if (spaceRight < tooltipRect.width + spacing) {
          newPlacement = 'left';
        }
      } else if (placement === 'bottomRight') {
        const spaceBelow = window.innerHeight - targetRect.bottom;
        if (spaceBelow < tooltipRect.height + spacing) {
          newPlacement = 'topRight';
        }
      } else if (placement === 'bottomLeft') {
        const spaceBelow = window.innerHeight - targetRect.bottom;
        if (spaceBelow < tooltipRect.height + spacing) {
          newPlacement = 'topLeft';
        }
      } else if (placement === 'topRight') {
        const spaceAbove = targetRect.top;
        if (spaceAbove < tooltipRect.height + spacing) {
          newPlacement = 'bottomRight';
        }
      } else if (placement === 'topLeft') {
        const spaceAbove = targetRect.top;
        if (spaceAbove < tooltipRect.height + spacing) {
          newPlacement = 'bottomLeft';
        }
      }

      setActualPlacement(newPlacement);

      let top = 0;
      let left = 0;
      let arrowLeft = '50%';
      let arrowTop = '0';

      switch (newPlacement) {
        case 'bottomRight':
          top = targetRect.bottom + 5;
          left = targetRect.right - tooltipRect.width + 33;
          // Calculate arrow position relative to tooltip
          arrowLeft = `${targetRect.right - left - targetRect.width / 2}px`;
          arrowTop = '-12px';
          break;
        case 'bottomLeft':
          top = targetRect.bottom + 5;
          left = targetRect.left - 23;
          arrowLeft = `${targetRect.left - left + targetRect.width / 2}px`;
          arrowTop = '-12px';
          break;
        case 'bottom':
          top = targetRect.bottom + 5;
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
          arrowLeft = '50%';
          arrowTop = '-12px';
          break;
        case 'topRight':
          top = targetRect.top - tooltipRect.height - 5;
          left = targetRect.right - tooltipRect.width + 33;
          arrowLeft = `${targetRect.right - left - targetRect.width / 2}px`;
          arrowTop = '100%';
          break;
        case 'topLeft':
          top = targetRect.top - tooltipRect.height - 5;
          left = targetRect.left - 23;
          arrowLeft = `${targetRect.left - left + targetRect.width / 2}px`;
          arrowTop = '100%';
          break;
        case 'top':
          top = targetRect.top - tooltipRect.height - 5;
          left = targetRect.left + (targetRect.width - tooltipRect.width + 2) / 2;
          arrowLeft = '50%';
          arrowTop = '100%';
          break;
        case 'left':
          top = targetRect.top + (targetRect.height - tooltipRect.height - 4) / 2;
          left = targetRect.left - tooltipRect.width - 5;
          arrowLeft = '100%';
          arrowTop = '50%';
          break;
        case 'right':
          top = targetRect.top + (targetRect.height - tooltipRect.height - 4) / 2;
          left = targetRect.right + 5;
          arrowLeft = '-12px';
          arrowTop = '50%';
          break;
      }
      // Ensure tooltip stays within viewport
      const viewport = {
        right: window.innerWidth - spacing,
        bottom: window.innerHeight - spacing
      };

      // Adjust tooltip position if it goes outside viewport
      if (left + tooltipRect.width > viewport.right) {
        // const oldLeft = left;
        left = viewport.right - tooltipRect.width;
        // Readjust arrow position
        arrowLeft = `${targetRect.left - left + targetRect.width / 2}px`;
      }
      if (left < spacing) {
        left = spacing;
        // Readjust arrow position
        arrowLeft = `${targetRect.left - left + targetRect.width / 2}px`;
      }

      tooltipRef.current.style.top = `${top}px`;
      tooltipRef.current.style.left = `${left}px`;
      setArrowPosition({ left: arrowLeft, top: arrowTop });
    };

    if (isVisible) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition);
      window.addEventListener('resize', calculatePosition);
    }

    return () => {
      window.removeEventListener('scroll', calculatePosition);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible, placement, actualPlacement]);

  const tooltipContent = (
    <TooltipContent
      ref={tooltipRef}
      placement={actualPlacement}
      arrowLeft={arrowPosition.left}
      arrowTop={arrowPosition.top}
      isShortCutKey={shortCutKey ? true : false}
      backgroundColor={
        themeMode?.theme === 'dark' ? 'var(--background-tooltip-tertiary)' : 'var(--background-tooltip-tertiary)'
      }
      isInModal={isInModal}
      maxWidth={maxWidth}
      className={className}>
      {title}
      {shortCutKey && <ShortCutKey>{shortCutKey}</ShortCutKey>}
    </TooltipContent>
  );

  return (
    <TooltipContainer>
      {/* onclick will only work for responsive devices */}
      <div
        role='button'
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}>
        {children}
      </div>
      {/* used CreatePortal for tooltips to avoid z-index and positioning issues when applied in modal */}
      {isVisible && (isInModal || isVisibleContent ? createPortal(tooltipContent, document.body) : tooltipContent)}
    </TooltipContainer>
  );
};

export default observer(Tooltip);
